import OfferStepper from './OfferStepper';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Chip, Typography } from '@mui/material';
import { OfferDetails } from './OfferDetails';
import { OfferItems } from './OfferItems';
import {
  OfferRouterParams,
  OfferState,
  OfferStep,
} from '../../../../shared/types/offers';
import { useOfferStore } from '../../stores/offerStore';
import LoadingRenderer from '../../components/Common/LoadingRenderer';
import { useOfferSheet } from '../../hooks/useOfferSheet';
import OfferStepActionButton from './OfferStepActionButton';
import { OfferPricing } from './OfferPricing';
import useSearchParams from '../../hooks/useSearchParams';
import NotificationProvider from '../../providers/Notification/NotificationProvider';
import { OfferItemDetails } from './OfferItemDetails';
import { OfferSummary } from './OfferSummary';
import { useUpdateOffer } from '../../hooks/useUpdateOffer';
import { useItems } from '../../hooks/useItems';
import GenericContainer from '../../GenericContainer';
import { colors } from '../../../../shared/constants';
import DefaultTooltip from '../../components/Tooltips/DefaultTooltip';
import LeadView from '../../components/Common/LeadView';
import { Box } from '@mui/system';
import { InfoOutlined } from '@mui/icons-material';
import { validateOfferDetails } from '../../helpers/offer';

const EditOffer = () => {
  const [activeStep, setActiveStep, clearCompareState] =
    useOfferStore((state) => [
      state.activeStep,
      state.setActiveStep,
      state.clearCompareState,
    ]);
  const params = useParams<OfferRouterParams>();
  const searchParams = useSearchParams();
  const history = useHistory();
  const setActiveStepFromSearchParams = useRef(true);
  const { id } = params;
  const { setNotification } = useContext(NotificationProvider);

  // fetch offer sheet
  const {
    data: offerSheet,
    isPending: isLoadingOffer,
    error,
    refetch,
  } = useOfferSheet({ id });

  // update offer sheet mutation
  const { mutateAsync: updateOffer, isPending: isUpdatingOffer } =
    useUpdateOffer();

  const { state } = offerSheet || {};
  // combined loading flag
  const isLoading = isLoadingOffer || isUpdatingOffer;

  // prefetch items if not already in cache, so that the Items step will load instantly
  useItems({
    includeHidden: true,
    offerSheetItemsOnly: true,
    offerSheet,
  });

  const isEditable =
    state === OfferState.Open ||
    state === OfferState.Draft ||
    state === OfferState.InternalApproval ||
    state === OfferState.InternalRejection;

  const isDetailsValid =
    !!offerSheet && validateOfferDetails(offerSheet);

  // clear compare state when leaving the page
  useEffect(() => () => clearCompareState(), [clearCompareState]);
  // redirect to offers listing if user isn't allowed to edit the offer,
  // the offer is removed or if the offer isn't allowed to be edited
  useEffect(() => {
    const status = error?.request?.status;
    if (status === 403) {
      history.push('/tarjoukset');
      setNotification({
        type: 'SNACKBAR',
        duration: 4000,
        severity: 'error',
        message:
          'Käyttäjätunnuksellasi ei ole oikeuksia muokata tarjousta!',
      });
    }
    if (offerSheet && (offerSheet.removedAt || !isEditable)) {
      history.push('/tarjoukset');
      setNotification({
        type: 'SNACKBAR',
        duration: 4000,
        severity: 'error',
        message: 'Tarjousta ei voi enää muokata!',
      });
    }
  }, [error, history, isEditable, offerSheet, setNotification]);

  useEffect(() => {
    if (offerSheet && state === OfferState.Open) {
      updateOffer({
        id: String(offerSheet.id),
        item: { state: OfferState.Draft },
      });
    }
  }, [offerSheet, state, updateOffer]);

  // update activeStep based on search params
  useEffect(() => {
    const step = searchParams.get('t');
    if (step && state && setActiveStepFromSearchParams.current) {
      // start from first page if user starts to edit "open" offer
      if (offerSheet?.state === OfferState.Open) {
        setActiveStep(OfferStep.Details);
        setActiveStepFromSearchParams.current = false;
        return;
      }

      if (!isDetailsValid) {
        setActiveStep(OfferStep.Details);
        setActiveStepFromSearchParams.current = false;
        return;
      }

      // check offer state and redirect to correct step is needed
      if (
        state === OfferState.InternalApproval &&
        Number(step) !== OfferStep.Summary
      ) {
        history.replace(
          `${history.location.pathname}?t=${OfferStep.Summary}`,
        );
        setActiveStep(OfferStep.Summary);
      } else {
        // // redirect to items step if offer has no items
        if (offerSheet?.items.length === 0 && step !== '1') {
          history.replace(
            `${history.location.pathname}?t=${OfferStep.Items}`,
          );
          setActiveStep(OfferStep.Items);
          return;
        }
        // otherwise set step from search params
        setActiveStep(Number(step));
      }
      setActiveStepFromSearchParams.current = false;
    }
  }, [
    history,
    isDetailsValid,
    setActiveStepFromSearchParams,
    searchParams,
    setActiveStep,
    state,
    offerSheet?.items,
    offerSheet?.previousState,
    offerSheet?.state,
    offerSheet?.lead,
  ]);

  const handleStepChange = useCallback(
    (nextStep: OfferStep) => {
      setActiveStep(nextStep);
      history.push(`${history.location.pathname}?t=${nextStep}`);
    },
    [history, setActiveStep],
  );

  const handleDisabledSteps = (step: number) => {
    if (state === OfferState.InternalApproval) {
      return step !== OfferStep.Summary;
    }
    return step > activeStep;
  };

  if (!offerSheet) {
    return (
      <GenericContainer borderColor={colors.ramiOrange}>
        {isLoadingOffer ? (
          <LoadingRenderer
            label={'Ladataan tarjousta'}
            sx={{ my: 2 }}
          />
        ) : (
          <Typography color={'primary'} variant={'h5'}>
            Tarjousta ei löytynyt!
          </Typography>
        )}
      </GenericContainer>
    );
  }

  const offerSheetItemCount = offerSheet.items.length;

  const isMissingItems =
    offerSheet?.lead?.leadProducts.filter(
      (leadProduct) => !leadProduct.isTransformed,
    ).length > 0;

  const LeadChip = () => (
    <DefaultTooltip
      overflow={'scroll'}
      backgroundColor={'white'}
      title={<LeadView lead={offerSheet.lead} />}
    >
      <Chip
        icon={<InfoOutlined />}
        sx={{ cursor: 'help' }}
        label={
          isMissingItems
            ? 'Luotu tarjouspyynnöstä (HUOM. Kaikki tuotteet eivät siirtyneet)'
            : 'Luotu tarjouspyynnöstä'
        }
        color={isMissingItems ? 'warning' : 'success'}
      />
    </DefaultTooltip>
  );

  return (
    <GenericContainer borderColor={colors.ramiOrange}>
      <OfferStepper
        onStepClick={handleStepChange}
        stepDisabled={handleDisabledSteps}
      />
      {/* OFFER DETAILS */}
      {activeStep === OfferStep.Details && (
        <OfferDetails
          offerSheet={offerSheet}
          renderStepActions={(state) => {
            const { pricingBasis } = state;
            return (
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                sx={{ width: '100%' }}
              >
                <Box></Box>
                <Box>{offerSheet?.lead && <LeadChip />}</Box>
                <Box>
                  <OfferStepActionButton
                    type={'NEXT'}
                    loading={isLoading}
                    disabled={!validateOfferDetails(state)}
                    onClick={async () => {
                      if (offerSheet.pricingBasis !== pricingBasis) {
                        setNotification({
                          type: 'MODAL',
                          size: 'md',
                          severity: 'info',
                          title: `Olet vaihtanut tarjouksen hinnoitteluperustetta. Haluatko jatkaa?`,
                          message: ` `,
                          NotificationBody: () => {
                            return (
                              <>
                                <Typography>
                                  Hinnoitteluperusteen vaihtaminen
                                  vaikuttaa valittuihin tuotteisiin.
                                  Jos olet vaihtamassa:
                                </Typography>
                                <ul style={{ textAlign: 'left' }}>
                                  {[
                                    'Päivä => Kuukausi: Valittujen tuotteiden hinnoitteluperusteeksi asetetaan "Kuukausi"',
                                    'Kuukausi => Päivä: Valittujen tuotteiden hinnoitteluperusteeksi asetetaan "Päivä"',
                                    'Päivä => Molemmat: Valituille päivätuotteille luodaan rinnalle "Kuukausi" -tuote',
                                    'Kuukausi => Molemmat: Valituille kuukausituotteille luodaan rinnalle "Päivä" -tuote',
                                  ].map((text) => (
                                    <li key={text}>
                                      <Typography>{text}</Typography>
                                    </li>
                                  ))}
                                  {[
                                    'Molemmat => Päivä: Valitut kuukausituotteet poistetaan hinnastosta',
                                    'Molemmat => Kuukausi: Valitut päivätuotteet poistetaan hinnastosta',
                                  ].map((text) => (
                                    <li key={text}>
                                      <Typography>
                                        <b>{text}</b>
                                      </Typography>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            );
                          },
                          onAccept: async () => {
                            await updateOffer({ id, item: state });
                            handleStepChange(OfferStep.Items);
                          },
                        });
                      } else {
                        await updateOffer({ id, item: state });
                        handleStepChange(OfferStep.Items);
                      }
                    }}
                  />
                </Box>
              </Box>
            );
          }}
        />
      )}
      {/* OFFER ITEMS */}
      {activeStep === OfferStep.Items && (
        <OfferItems
          offerSheet={offerSheet}
          renderStepActions={(itemCount, isUpdatingItems) => (
            <>
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                sx={{ width: '100%' }}
              >
                <Box>
                  <OfferStepActionButton
                    type={'BACK'}
                    loading={isLoading}
                    onClick={() =>
                      handleStepChange(OfferStep.Details)
                    }
                  />
                </Box>
                <Box>{offerSheet?.lead && <LeadChip />}</Box>
                <Box>
                  <OfferStepActionButton
                    type={'NEXT'}
                    loading={isLoading || isUpdatingItems}
                    disabled={itemCount === 0}
                    onClick={() => {
                      if (itemCount > 15) {
                        setNotification({
                          type: 'MODAL',
                          severity: 'info',
                          message: `Olet valinnut tarjoukselle ${itemCount} tuotetta. Oletko varma että haluat tehdä tarjouksen etkä hinnastoa?`,
                          title: 'Ennen kuin jatkat...',
                          onAccept: () =>
                            handleStepChange(OfferStep.ItemDetails),
                        });
                      } else {
                        handleStepChange(OfferStep.ItemDetails);
                      }
                    }}
                  />
                </Box>
              </Box>
            </>
          )}
        />
      )}
      {/* OFFER ITEM DETAILS */}
      {activeStep === OfferStep.ItemDetails && (
        <OfferItemDetails
          offerSheet={offerSheet}
          renderStepActions={() => (
            <>
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                sx={{ width: '100%' }}
              >
                <Box>
                  <OfferStepActionButton
                    type={'BACK'}
                    loading={isLoading}
                    onClick={() => handleStepChange(OfferStep.Items)}
                  />
                </Box>
                <Box>{offerSheet?.lead && <LeadChip />}</Box>
                <Box>
                  <OfferStepActionButton
                    type={'NEXT'}
                    loading={isLoading}
                    onClick={() =>
                      handleStepChange(OfferStep.Pricing)
                    }
                  />
                </Box>
              </Box>
            </>
          )}
        />
      )}
      {/* OFFER PRICING */}
      {activeStep === OfferStep.Pricing && (
        <OfferPricing
          offerSheet={offerSheet}
          renderStepActions={(
            contractSheet,
            offerItems,
            isLoadingExternalItems,
          ) => (
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              sx={{ width: '100%' }}
            >
              <Box>
                <OfferStepActionButton
                  type={'BACK'}
                  loading={isLoading}
                  onClick={() => {
                    handleStepChange(OfferStep.ItemDetails);
                  }}
                />
              </Box>
              <Box>{offerSheet?.lead && <LeadChip />}</Box>
              <Box>
                <OfferStepActionButton
                  type={'NEXT'}
                  disabled={
                    offerSheetItemCount === 0 ||
                    isLoadingExternalItems
                  }
                  loading={isLoading}
                  onClick={() => {
                    const onNext = () => {
                      handleStepChange(OfferStep.Summary);
                      // Fetch offer totals which are counted on server
                      refetch();
                    };

                    const validateContractPrices = () => {
                      if (!contractSheet.items) {
                        return false;
                      }

                      return offerItems.some((item) => {
                        if (!contractSheet.items) {
                          return false;
                        }

                        const contractItem =
                          contractSheet.items[item.catClass];
                        if (
                          item.pricingBasis === 'DAY' &&
                          contractItem?.dayPrice &&
                          item?.dayPrice &&
                          contractItem.dayPrice < item.dayPrice
                        ) {
                          return true;
                        } else if (
                          item.pricingBasis === 'MONTH' &&
                          contractItem?.monthPrice &&
                          item?.monthPrice &&
                          contractItem.monthPrice < item.monthPrice
                        ) {
                          return true;
                        } else {
                          return false;
                        }
                      });
                    };

                    if (validateContractPrices()) {
                      setNotification({
                        type: 'MODAL',
                        severity: 'info',
                        message: `Tarjouksella on tuotteita joiden tarjoushinta on korkeampi kuin sopimushinnaston, haluatko jatkaa?`,
                        title: 'Ennen kuin jatkat...',
                        onAccept: () => {
                          onNext();
                        },
                      });
                    } else {
                      onNext();
                    }
                  }}
                />
              </Box>
            </Box>
          )}
        />
      )}
      {/* OFFER SUMMARY */}
      {activeStep === OfferStep.Summary && (
        <OfferSummary
          offerSheet={offerSheet}
          renderStepActions={() => (
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              sx={{ width: '100%' }}
            >
              <Box>
                <OfferStepActionButton
                  type={'BACK'}
                  disabled={state === OfferState.InternalApproval}
                  loading={isLoading}
                  onClick={() => handleStepChange(OfferStep.Pricing)}
                />
              </Box>
              <Box>{offerSheet?.lead && <LeadChip />}</Box>
              <Box></Box>
            </Box>
          )}
        />
      )}
    </GenericContainer>
  );
};

export default EditOffer;
